import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Coins,
  Hourglass,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { OrganizationFilter } from '../../components/OrganizationFilter';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { executionTriggerLabel } from '../../components/ui/TriggerTypeBadge';
import { canSeeAiCost, formatEur, formatTokens } from '../../lib/ai-cost';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';
import { MyAutomationsSection } from '../automations/MyAutomationsSection';
import { useUiPreferences } from '../preferences/ui-preferences';

type Period = '7d' | '30d' | '90d' | '12m';

const PERIOD_LABEL: Record<Period, string> = {
  '7d': '7 j',
  '30d': '30 j',
  '90d': '90 j',
  '12m': '12 mois',
};

interface Overview {
  counts: {
    activeAutomations: number;
    executionsTotal: number;
    succeeded: number;
    failed: number;
    successRate: number;
    openIncidents: number;
  };
  totals: {
    estimatedMinutesSaved: number;
    /** Présents uniquement pour le super-administrateur. */
    aiCostEur?: number;
    aiTokens?: number;
  };
}

interface TrendPoint {
  periodStart: string;
  executions: number;
  succeeded: number;
  failed: number;
  estimatedMinutesSaved: number;
}

interface TrendsResponse {
  granularity: 'day' | 'week' | 'month';
  points: TrendPoint[];
}

interface AutomationRow {
  automationId: string;
  name: string;
  executions: number;
  successRate: number;
  estimatedMinutesSaved: number;
  aiCostEur?: number;
}

interface TriggerRow {
  trigger: string;
  count: number;
}

interface OrgRow {
  organizationId: string;
  name: string;
  executions: number;
  successRate: number;
  estimatedMinutesSaved: number;
  aiCostEur?: number;
}

interface ReportRow {
  id: string;
  type: 'WEEKLY' | 'MONTHLY' | 'CUSTOM';
  format: string;
  periodStart: string;
  periodEnd: string;
  filePath: string;
  /** Rapport interne avec coût IA (listé pour le seul super-administrateur). */
  includeAiCost?: boolean;
  createdAt: string;
}

interface RecentExecution {
  id: string;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'PARTIAL';
  startedAt: string;
  automation: { name: string };
}

function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  alert = false,
}: {
  label: string;
  value: string;
  hint?: string;
  icon: LucideIcon;
  /** Valeur à surveiller (ex. incidents ouverts > 0) : affichée en rouge. */
  alert?: boolean;
}) {
  return (
    <div className="surface p-4 sm:p-5">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
        <Icon className={`h-4 w-4 ${alert ? 'text-red-600' : 'text-accent'}`} aria-hidden="true" />
        {label}
      </div>
      <div
        className={`mt-2 text-xl font-bold tabular-nums tracking-tight sm:text-2xl ${alert ? 'text-red-700' : 'text-slate-900'}`}
      >
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

interface OrgOption {
  id: string;
  name: string;
  type: 'ADWEB' | 'CLIENT';
}

type ReportType = 'WEEKLY' | 'MONTHLY' | 'CUSTOM';

// Libellés français des types de rapport (CUSTOM = fenêtre glissante de 30 jours).
const TYPE_LABEL: Record<ReportType, string> = {
  WEEKLY: 'Hebdomadaire',
  MONTHLY: 'Mensuel',
  CUSTOM: '30 derniers jours',
};

export function KpisPage() {
  const [period, setPeriod] = useState<Period>('30d');
  const qc = useQueryClient();
  const { user } = useAuth();
  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';
  // Coût IA remonté par n8n : super-administrateur uniquement.
  const showAiCost = canSeeAiCost(user?.role);
  // Couleurs des graphiques (Recharts n'accepte pas les variables CSS en attribut SVG).
  const { uiTheme } = useUiPreferences();
  const chart =
    uiTheme === 'SOMBRE'
      ? { grid: '#243049', text: '#94a3b8', tooltipBg: '#121a2b', tooltipBorder: '#2a3650' }
      : { grid: '#e2e8f0', text: '#64748b', tooltipBg: '#ffffff', tooltipBorder: '#e2e8f0' };
  const tooltipStyle = {
    backgroundColor: chart.tooltipBg,
    border: `1px solid ${chart.tooltipBorder}`,
    borderRadius: 8,
    color: chart.text,
  };
  // /reports est réservé aux rôles Adweb et CLIENT_ADMIN (l'API répond 403 sinon).
  const canReport = isPlatform || user?.role === 'CLIENT_ADMIN';
  // L'organisation ciblée par les rapports : obligatoire pour un rôle plateforme
  // (l'API rejette sinon). Défaut = organisation de l'utilisateur courant.
  const [reportOrgId, setReportOrgId] = useState(user?.organizationId ?? '');
  const [reportType, setReportType] = useState<ReportType>('WEEKLY');
  const [reportFormat, setReportFormat] = useState<'pdf' | 'csv'>('pdf');
  const [reportWithAi, setReportWithAi] = useState(false);
  // Filtre organisation (rôles Adweb uniquement) — '' = toutes. Le serveur
  // ignore ce paramètre pour les rôles clients.
  const [orgFilter, setOrgFilter] = useState('');
  const orgParams = { period, ...(orgFilter ? { organizationId: orgFilter } : {}) };

  function handleOrgFilter(id: string) {
    setOrgFilter(id);
    // Les rapports suivent l'organisation filtrée, pour éviter d'en générer un
    // sur une autre organisation que celle affichée.
    if (id) setReportOrgId(id);
  }

  const reportOrgs = useQuery({
    queryKey: ['organizations', 'for-report'],
    queryFn: async () =>
      (await apiClient.get<{ data: OrgOption[] }>('/organizations?pageSize=100')).data.data,
    enabled: isPlatform,
  });

  const overview = useQuery({
    queryKey: ['kpis', 'overview', period, orgFilter],
    queryFn: async () =>
      (await apiClient.get<Overview>('/kpis/overview', { params: orgParams })).data,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
  const trends = useQuery({
    queryKey: ['kpis', 'trends', period, orgFilter],
    queryFn: async () =>
      (await apiClient.get<TrendsResponse>('/kpis/trends', { params: orgParams })).data,
    placeholderData: keepPreviousData,
  });
  const byAuto = useQuery({
    queryKey: ['kpis', 'by-automation', period, orgFilter],
    queryFn: async () =>
      (await apiClient.get<AutomationRow[]>('/kpis/by-automation', { params: orgParams })).data,
    placeholderData: keepPreviousData,
  });
  const byTrigger = useQuery({
    queryKey: ['kpis', 'by-trigger', period, orgFilter],
    queryFn: async () =>
      (await apiClient.get<TriggerRow[]>('/kpis/by-trigger', { params: orgParams })).data,
    placeholderData: keepPreviousData,
  });
  const byOrg = useQuery({
    queryKey: ['kpis', 'by-organization', period],
    queryFn: async () =>
      (await apiClient.get<OrgRow[]>(`/kpis/by-organization?period=${period}`)).data,
    retry: false,
  });

  // Dernières exécutions (aperçu de la page Exécutions, repris depuis l'ancienne Synthèse).
  const recentExecs = useQuery({
    queryKey: ['executions', 'recent-preview', orgFilter],
    queryFn: async () =>
      (
        await apiClient.get<{ data: RecentExecution[] }>('/executions', {
          params: { page: 1, pageSize: 5, ...(orgFilter ? { organizationId: orgFilter } : {}) },
        })
      ).data.data,
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  });

  const reports = useQuery({
    queryKey: ['reports', 'list'],
    enabled: canReport,
    queryFn: async () => (await apiClient.get<{ data: ReportRow[] }>('/reports')).data,
    // Tant qu'au moins un rapport est en cours de génération (`filePath === 'pending'`),
    // on rafraîchit la liste toutes les 3 s pour faire apparaître automatiquement
    // le bouton « Télécharger » dès que le worker a fini.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.data.some((r) => r.filePath === 'pending') ? 3000 : false;
    },
  });

  const generate = useMutation({
    mutationFn: async (input: { type: 'WEEKLY' | 'MONTHLY' | 'CUSTOM'; format: 'pdf' | 'csv' }) => {
      const organizationId = reportOrgId || user?.organizationId;
      return apiClient.post('/reports/generate', {
        ...input,
        organizationId,
        ...(showAiCost && reportWithAi ? { includeAiCost: true } : {}),
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['reports', 'list'] });
    },
  });

  // Téléchargement authentifié : l'endpoint exige le bearer, donc on récupère le
  // fichier en blob via apiClient (un simple <a href> partirait sans token → 401).
  const download = useMutation({
    mutationFn: async (r: ReportRow) => {
      const res = await apiClient.get(`/reports/${r.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-${r.id}.${r.format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });

  const retry = useMutation({
    mutationFn: async (r: ReportRow) => apiClient.post(`/reports/${r.id}/retry`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['reports', 'list'] });
    },
  });

  const apiErrorMessage = (err: unknown): string => {
    const data = (err as { response?: { data?: { message?: string } } } | null)?.response?.data;
    return data?.message ?? 'Une erreur est survenue. Réessayez dans un instant.';
  };

  const formatMinutes = (m: number) => {
    if (m >= 60) return `${(m / 60).toFixed(1)} h`;
    return `${m} min`;
  };
  const formatPct = (p: number) => `${(p * 100).toFixed(1)} %`;

  return (
    <>
      <PageHeader
        helpTopic="synthese"
        title="Synthèse"
        subtitle="Vue d'ensemble de vos automatisations et de la valeur produite."
        actions={
          <>
            {isPlatform && (
              <OrganizationFilter value={orgFilter} onChange={handleOrgFilter} inHeader />
            )}
            {(['7d', '30d', '90d', '12m'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                aria-pressed={period === p}
                className="hdr-ctl"
              >
                {PERIOD_LABEL[p]}
              </button>
            ))}
          </>
        }
      />

      {/* Rôles clients : leurs automatisations d'abord, pour les lancer sans chercher. */}
      {!isPlatform && <MyAutomationsSection />}

      <section
        className={`grid grid-cols-2 gap-3 px-4 py-6 sm:gap-4 sm:px-6 lg:px-8 ${showAiCost ? 'lg:grid-cols-3 2xl:grid-cols-6' : isPlatform ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}
      >
        <KpiCard
          icon={Zap}
          label="Automatisations actives"
          value={String(overview.data?.counts.activeAutomations ?? '—')}
        />
        <KpiCard
          icon={Activity}
          label="Exécutions"
          value={String(overview.data?.counts.executionsTotal ?? '—')}
        />
        <KpiCard
          icon={CheckCircle2}
          label="Taux de succès"
          value={overview.data ? formatPct(overview.data.counts.successRate) : '—'}
          hint={
            overview.data
              ? `${overview.data.counts.succeeded} succès / ${overview.data.counts.failed} échecs`
              : ''
          }
        />
        <KpiCard
          icon={Hourglass}
          label="Temps gagné"
          value={overview.data ? formatMinutes(overview.data.totals.estimatedMinutesSaved) : '—'}
        />
        {isPlatform && (
          <KpiCard
            icon={AlertTriangle}
            label="Incidents ouverts"
            value={String(overview.data?.counts.openIncidents ?? '—')}
            alert={(overview.data?.counts.openIncidents ?? 0) > 0}
          />
        )}
        {showAiCost && (
          <KpiCard
            icon={Coins}
            label="Coût IA"
            value={overview.data ? formatEur(overview.data.totals.aiCostEur ?? 0) : '—'}
            hint={
              overview.data
                ? `${formatTokens(overview.data.totals.aiTokens ?? 0)} jetons · visible par vous seul`
                : ''
            }
          />
        )}
      </section>

      {/* Aperçu « Dernières exécutions » — fusionné depuis l'ancienne page Synthèse. */}
      <section className="px-4 sm:px-6 lg:px-8 pb-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium text-slate-700">Dernières exécutions</h2>
          <Link to="/executions" className="text-xs font-medium text-brand hover:underline">
            Voir le journal complet →
          </Link>
        </div>
        <div className="overflow-x-auto surface">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Automatisation</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Démarrée</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(recentExecs.data ?? []).map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-2.5">{e.automation.name}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={e.status} />
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">
                    {new Date(e.startedAt).toLocaleString('fr-FR')}
                  </td>
                </tr>
              ))}
              {recentExecs.data?.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-slate-400">
                    Aucune exécution récente.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 px-4 sm:px-6 lg:px-8 pb-6 lg:grid-cols-2">
        <div className="surface p-5">
          <h2 className="mb-3 text-sm font-medium text-slate-700">Exécutions par période</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trends.data?.points ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="periodStart" tick={{ fontSize: 11, fill: chart.text }} />
                <YAxis tick={{ fontSize: 11, fill: chart.text }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="succeeded"
                  stroke="#10b981"
                  name="Succès"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="failed"
                  stroke="#ef4444"
                  name="Échecs"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="surface p-5">
          <h2 className="mb-3 text-sm font-medium text-slate-700">Temps gagné par période</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trends.data?.points ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="periodStart" tick={{ fontSize: 11, fill: chart.text }} />
                <YAxis tick={{ fontSize: 11, fill: chart.text }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${v} min`} />
                <Bar dataKey="estimatedMinutesSaved" fill="#1F4FFF" name="Minutes gagnées" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Section auto-masquée : on n'affiche la répartition que s'il y a au moins
          2 types de déclencheurs avec exécutions sur la période. Sinon c'est juste
          du bruit visuel (1 barre à 100 % dans un parc mono-type). */}
      {(byTrigger.data ?? []).filter((r) => r.count > 0).length >= 2 && (
        <section className="px-4 sm:px-6 lg:px-8 pb-6">
          <h2 className="mb-3 text-sm font-medium text-slate-700">Répartition par déclencheur</h2>
          <div className="surface p-5">
            {(() => {
              const rows = byTrigger.data ?? [];
              const total = rows.reduce((acc, r) => acc + r.count, 0);
              return (
                <ul className="space-y-2">
                  {rows.map((r) => {
                    const pct = Math.round((r.count / total) * 100);
                    return (
                      <li key={r.trigger} className="flex items-center gap-3 text-sm">
                        <span className="w-24 shrink-0 text-slate-700">
                          {executionTriggerLabel(r.trigger)}
                        </span>
                        <span className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <span
                            className="block h-full rounded-full bg-brand"
                            style={{ width: `${pct}%` }}
                          />
                        </span>
                        <span className="w-20 shrink-0 text-right text-slate-500">
                          {r.count} ({pct} %)
                        </span>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </div>
        </section>
      )}

      <section className="px-4 sm:px-6 lg:px-8 pb-6">
        <h2 className="mb-3 text-sm font-medium text-slate-700">Par automatisation</h2>
        <div className="overflow-x-auto surface">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3">Exécutions</th>
                <th className="px-4 py-3">Taux de succès</th>
                <th className="px-4 py-3">Temps gagné</th>
                {showAiCost && <th className="px-4 py-3">Coût IA</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(byAuto.data ?? []).map((r) => (
                <tr key={r.automationId}>
                  <td className="px-4 py-2.5 text-slate-900">{r.name}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.executions}</td>
                  <td className="px-4 py-2.5 text-slate-600">{formatPct(r.successRate)}</td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {formatMinutes(r.estimatedMinutesSaved)}
                  </td>
                  {showAiCost && (
                    <td className="px-4 py-2.5 tabular-nums text-slate-600">
                      {formatEur(r.aiCostEur)}
                    </td>
                  )}
                </tr>
              ))}
              {byAuto.data?.length === 0 && (
                <tr>
                  <td colSpan={showAiCost ? 5 : 4} className="px-4 py-6 text-center text-slate-400">
                    Aucune donnée sur la période.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {!orgFilter && byOrg.data && byOrg.data.length > 0 && (
        <section className="px-4 sm:px-6 lg:px-8 pb-6">
          <h2 className="mb-3 text-sm font-medium text-slate-700">
            Par organisation (Adweb-Systèmes)
          </h2>
          <div className="overflow-x-auto surface">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Organisation</th>
                  <th className="px-4 py-3">Exécutions</th>
                  <th className="px-4 py-3">Taux de succès</th>
                  <th className="px-4 py-3">Temps gagné</th>
                  {showAiCost && <th className="px-4 py-3">Coût IA</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {byOrg.data.map((o) => (
                  <tr key={o.organizationId}>
                    <td className="px-4 py-2.5 text-slate-900">{o.name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{o.executions}</td>
                    <td className="px-4 py-2.5 text-slate-600">{formatPct(o.successRate)}</td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {formatMinutes(o.estimatedMinutesSaved)}
                    </td>
                    {showAiCost && (
                      <td className="px-4 py-2.5 tabular-nums text-slate-600">
                        {formatEur(o.aiCostEur)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {canReport && (
        <section data-help="reports" className="px-4 sm:px-6 lg:px-8 pb-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-medium text-slate-700">Rapports</h2>
            <div className="flex flex-wrap items-center gap-2">
              {isPlatform && (
                <select
                  aria-label="Organisation du rapport"
                  value={reportOrgId}
                  onChange={(e) => setReportOrgId(e.target.value)}
                  className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700"
                >
                  {(reportOrgs.data ?? []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                      {o.type === 'ADWEB' ? ' — interne' : ''}
                    </option>
                  ))}
                </select>
              )}
              <select
                aria-label="Période du rapport"
                value={reportType}
                onChange={(e) => setReportType(e.target.value as ReportType)}
                className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700"
              >
                <option value="WEEKLY">Hebdomadaire (7 j)</option>
                <option value="MONTHLY">Mensuel (1 mois)</option>
              </select>
              <select
                aria-label="Format du rapport"
                value={reportFormat}
                onChange={(e) => setReportFormat(e.target.value as 'pdf' | 'csv')}
                className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700"
              >
                <option value="pdf">PDF</option>
                <option value="csv">CSV</option>
              </select>
              {showAiCost && (
                <label
                  className="flex items-center gap-1.5 text-xs text-slate-600"
                  title="Rapport interne : visible et téléchargeable par les seuls super-administrateurs"
                >
                  <input
                    type="checkbox"
                    checked={reportWithAi}
                    onChange={(e) => setReportWithAi(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  Coût IA (interne)
                </label>
              )}
              <button
                disabled={generate.isPending}
                onClick={() => generate.mutate({ type: reportType, format: reportFormat })}
                className="rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {generate.isPending ? 'Génération…' : 'Générer'}
              </button>
            </div>
          </div>
          {(generate.isError || download.isError || retry.isError) && (
            <div
              role="alert"
              className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              {apiErrorMessage(generate.error ?? download.error ?? retry.error)}
            </div>
          )}
          {download.isPending && (
            <div className="mb-3 text-xs text-slate-500">Téléchargement en cours…</div>
          )}
          <div className="overflow-x-auto surface">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Période</th>
                  <th className="hidden px-4 py-3 sm:table-cell">Format</th>
                  <th className="hidden px-4 py-3 md:table-cell">Généré le</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(reports.data?.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2.5">
                      {TYPE_LABEL[r.type] ?? r.type}
                      {r.includeAiCost && (
                        <span className="ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                          Interne · coût IA
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {new Date(r.periodStart).toLocaleDateString('fr-FR')} →{' '}
                      {new Date(r.periodEnd).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="hidden px-4 py-2.5 uppercase text-slate-600 sm:table-cell">
                      {r.format}
                    </td>
                    <td className="hidden px-4 py-2.5 text-slate-500 md:table-cell">
                      {new Date(r.createdAt).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-4 py-2.5">
                      {r.filePath === 'pending' ? (
                        <span className="text-xs text-slate-400">En préparation…</span>
                      ) : r.filePath === 'failed' ? (
                        <span className="flex items-center gap-3 text-xs">
                          <span className="font-medium text-red-600">Échec</span>
                          <button
                            type="button"
                            disabled={retry.isPending}
                            onClick={() => retry.mutate(r)}
                            className="font-medium text-brand hover:underline disabled:opacity-50"
                          >
                            Relancer
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          disabled={download.isPending}
                          onClick={() => download.mutate(r)}
                          className="text-xs font-medium text-brand hover:underline disabled:opacity-50"
                        >
                          Télécharger
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {reports.data?.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                      Aucun rapport généré.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
